/**
 * /bank-accounts — multi-bank, multi-currency cash management.
 *
 * Top: list of cards, one per bank account. Each card shows bank name,
 * last 4, currency, current balance (from the GL).
 *
 * Click a card → drawer opens with the transaction ledger (deposits,
 * withdrawals, transfers) and actions (+ Deposit, + Withdrawal,
 * + Transfer).
 *
 * Deposit / Withdrawal dialogs support PDF drop for supporting doc
 * (wire scans, loan draw advices, deposit slips). Upload posts first,
 * returns URL + sha256, then the actual deposit/withdrawal API call
 * records the doc on the journal.
 */
import { Link, createFileRoute, useLocation } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, TextField,
} from "@mui/material";

import { api, getToken } from "@/lib/api";
import {
  accountKeys,
  accountsListQueryOptions,
  bankAccountKeys,
  bankAccountTransactionsQueryOptions,
  bankAccountsListQueryOptions,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import { MoneyField } from "@/components/MoneyField";
import { SuggestButton } from "@/components/SuggestButton";
import type { ProposedCreate } from "@/lib/suggest";
import type {
  BankAccount, BankAccountCreate, BankAccountType,
  BankTransaction,
  UploadDocumentResponse,
} from "@/lib/types";
import { codeAndName } from "../lib/displayCode";


export const Route = createFileRoute("/_auth/bank-accounts/")({
  component: BankAccountsPage,
});


const ACCOUNT_TYPES: { value: BankAccountType; label: string; hint: string }[] = [
  { value: "checking",     label: "Checking",      hint: "Operating / AP / payroll" },
  { value: "savings",      label: "Savings",       hint: "Reserve funds" },
  { value: "money_market", label: "Money Market",  hint: "Interest-bearing idle cash" },
  { value: "credit_line",  label: "Line of Credit", hint: "Revolving debt facility" },
  { value: "loan",         label: "Loan",          hint: "Term loan / mortgage" },
  { value: "other",        label: "Other",         hint: "" },
];


function BankAccountsPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const banksQuery = useQuery(bankAccountsListQueryOptions());
  const { hash } = useLocation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newBankOpen, setNewBankOpen] = useState(false);

  const banks = banksQuery.data ?? [];
  const focus = hash === "loans" || hash === "short-term-investments" ? hash : null;
  const visibleBanks = focus === "loans"
    ? banks.filter((bank) => bank.account_type === "loan" || bank.account_type === "credit_line")
    : focus === "short-term-investments"
      ? banks.filter((bank) => {
        if (bank.account_type === "money_market") return true;
        if (bank.account_type !== "other") return false;
        return /\b(cd|certificate of deposit|treasury|t-?bill)\b/i.test(`${bank.name} ${bank.bank_name}`);
      })
      : banks;
  const focusTitle = focus === "loans"
    ? "Loan balances and forecasts"
    : focus === "short-term-investments"
      ? "CDs and short-term investments"
      : null;
  const focusDescription = focus === "loans"
    ? "Loan and credit-line accounts, with their current ledger balances and transaction history."
    : focus === "short-term-investments"
      ? "Money-market accounts and CDs recorded as other bank accounts, with current ledger balances and transaction history."
      : null;
  const selected = banks.find((b) => b.id === selectedId) ?? null;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Bank Accounts</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {visibleBanks.length} account{visibleBanks.length === 1 ? "" : "s"} · multi-currency
          </span>
          <div className="flex-1" />
          <button
            onClick={() => setNewBankOpen(true)}
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            + New bank account
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {focusTitle && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-900 dark:bg-blue-950/40">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h2 className="font-semibold text-slate-900 dark:text-slate-100">{focusTitle}</h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{focusDescription}</p>
              </div>
              <Link
                to="/bank-accounts"
                className="shrink-0 text-sm font-medium text-blue-700 hover:underline dark:text-blue-300"
              >
                View all accounts
              </Link>
            </div>
          </div>
        )}
        {banksQuery.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}
        {visibleBanks.length === 0 && !banksQuery.isLoading && (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center dark:bg-gray-900 dark:border-gray-700">
            <div className="text-slate-600 font-medium dark:text-slate-300">
              {focusTitle ? `No ${focusTitle.toLowerCase()} configured yet.` : "No bank accounts yet."}
            </div>
            <div className="text-xs text-slate-500 mt-1 dark:text-slate-400">
              Click + New bank account to add one.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleBanks.map((b) => (
            <BankCard key={b.id} bank={b} onClick={() => setSelectedId(b.id)} />
          ))}
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <BankDetailDrawer
          bank={selected}
          onClose={() => setSelectedId(null)}
          onAny={() => {
            qc.invalidateQueries({ queryKey: bankAccountKeys.all });
          }}
        />
      )}

      {newBankOpen && (
        <NewBankAccountDialog
          open={newBankOpen}
          onClose={() => setNewBankOpen(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: bankAccountKeys.all });
            toast.success("Bank account created");
          }}
        />
      )}
    </div>
  );
}


function BankCard({ bank, onClick }: { bank: BankAccount; onClick: () => void }) {
  const balance = Number(bank.current_balance ?? 0);
  const isCredit = bank.account_type === "credit_line" || bank.account_type === "loan";

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white border border-slate-200 rounded-lg p-4 hover:border-blue-400 hover:shadow transition dark:bg-gray-900 dark:border-gray-800"
    >
      <div className="flex items-baseline gap-2">
        <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {bank.account_type.replace("_", " ")}
        </div>
        <div className="flex-1" />
        <div className="text-xs text-slate-400 font-mono dark:text-slate-500">
          {bank.currency_code}
        </div>
      </div>
      <div className="mt-1 font-semibold text-slate-900 dark:text-slate-100">{bank.name}</div>
      <div className="text-xs text-slate-600 dark:text-slate-300">{bank.bank_name}</div>
      {bank.account_number_last4 && (
        <div className="text-xs text-slate-500 font-mono mt-0.5 dark:text-slate-400">
          •••{bank.account_number_last4}
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-gray-800">
        <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {isCredit ? "Outstanding" : "Balance"}
        </div>
        <div className={`text-xl font-mono font-semibold ${
          balance < 0 ? "text-red-700"
            : isCredit ? "text-amber-700"
            : "text-emerald-700"
        }`}>
          {balance.toLocaleString(undefined, {
            style: "currency", currency: bank.currency_code,
            minimumFractionDigits: 2, maximumFractionDigits: 2,
          })}
        </div>
      </div>
      <div className="mt-2 text-xs text-slate-400 dark:text-slate-500">
        GL: {codeAndName({ code: bank.gl_account_code, name: bank.gl_account_name }, " — ")}
      </div>
    </button>
  );
}


function BankDetailDrawer({
  bank, onClose, onAny,
}: {
  bank: BankAccount;
  onClose: () => void;
  onAny: () => void;
}) {
  const txQuery = useQuery(bankAccountTransactionsQueryOptions(bank.id));
  const [action, setAction] = useState<null | "deposit" | "withdraw">(null);

  return (
    <div className="fixed inset-0 bg-slate-900/30 z-40 flex justify-end"
         onClick={onClose}>
      <div className="w-[640px] max-w-full h-full bg-white shadow-lg flex flex-col dark:bg-gray-900"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3 dark:border-gray-800">
          <div className="flex-1">
            <div className="font-semibold text-slate-900 dark:text-slate-100">{bank.name}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {bank.bank_name}
              {bank.account_number_last4 && ` · •••${bank.account_number_last4}`}
              {" · "}{bank.currency_code}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 px-2 dark:text-slate-500">✕</button>
        </div>

        {/* Balance + actions */}
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-4 dark:bg-gray-950 dark:border-gray-800">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Current balance</div>
            <div className="text-xl font-mono font-semibold text-slate-900 dark:text-slate-100">
              {Number(bank.current_balance ?? 0).toLocaleString(undefined, {
                style: "currency", currency: bank.currency_code,
                minimumFractionDigits: 2, maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div className="flex-1" />
          <button
            onClick={() => setAction("deposit")}
            className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            + Deposit
          </button>
          <button
            onClick={() => setAction("withdraw")}
            className="text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            − Withdraw
          </button>
        </div>

        {/* Transactions */}
        <div className="flex-1 overflow-auto px-5 py-3">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-2 dark:text-slate-400">
            Transactions
          </div>
          {txQuery.isLoading && <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>}
          {(txQuery.data?.length ?? 0) === 0 && !txQuery.isLoading && (
            <div className="text-sm text-slate-500 dark:text-slate-400">
              No transactions yet. Click + Deposit to record the first one.
            </div>
          )}
          {(txQuery.data ?? []).slice().reverse().map((t: BankTransaction) => (
            <div
              key={t.journal_id}
              className="border-b border-slate-100 py-2 flex items-baseline gap-3 text-sm dark:border-gray-800"
            >
              <div className="text-xs text-slate-500 font-mono w-20 shrink-0 dark:text-slate-400">
                {t.journal_date}
              </div>
              <div className="flex-1 min-w-0">
                <div className="truncate text-slate-800 dark:text-slate-200">{t.description}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  J#{t.journal_no} · {t.source}
                  {t.source_document_url && (
                    <> · <a className="text-blue-600 hover:underline"
                            href={t.source_document_url}
                            target="_blank" rel="noreferrer">doc</a></>
                  )}
                </div>
              </div>
              <div className={`font-mono text-sm text-right w-28 shrink-0 ${
                Number(t.debit) > 0 ? "text-emerald-700" : "text-red-700"
              }`}>
                {Number(t.debit) > 0
                  ? `+${Number(t.debit).toFixed(2)}`
                  : `−${Number(t.credit).toFixed(2)}`}
              </div>
              <div className="font-mono text-xs text-slate-500 w-28 shrink-0 text-right dark:text-slate-400">
                {Number(t.running_balance).toFixed(2)}
              </div>
            </div>
          ))}
        </div>

        {action && (
          <DepositWithdrawDialog
            bank={bank}
            kind={action}
            onClose={() => setAction(null)}
            onPosted={() => {
              setAction(null);
              onAny();
            }}
          />
        )}
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Deposit / Withdrawal dialog (with PDF drop)
// ──────────────────────────────────────────────────────────────
function DepositWithdrawDialog({
  bank, kind, onClose, onPosted,
}: {
  bank: BankAccount;
  kind: "deposit" | "withdraw";
  onClose: () => void;
  onPosted: () => void;
}) {
  const toast = useToast();
  const isDeposit = kind === "deposit";
  const [amount, setAmount] = useState("");
  const [counterpartyCode, setCounterpartyCode] = useState(
    isDeposit ? "2200" : "6999",
  );
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [memo, setMemo] = useState("");
  const [fxRate, setFxRate] = useState("");
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [docSha, setDocSha] = useState<string | null>(null);
  const [docName, setDocName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const uploadDoc = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const r = await fetch("/api/bank-accounts/upload-document", {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken() ?? ""}` },
        body: fd,
      });
      if (!r.ok) {
        let detail = `${r.status} ${r.statusText}`;
        try {
          const j = await r.json();
          detail = j.detail ?? detail;
        } catch {
          // not json
        }
        throw new Error(detail);
      }
      const result = (await r.json()) as UploadDocumentResponse;
      setDocUrl(result.url);
      setDocSha(result.sha256);
      setDocName(result.filename);
      toast.success(`Uploaded ${result.filename}`);
    } catch (e) {
      toast.error(`Upload failed: ${(e as Error).message}`);
    } finally {
      setUploading(false);
    }
  };

  const post = useMutation({
    mutationFn: async () => {
      const url = isDeposit
        ? `/api/bank-accounts/${bank.id}/deposits`
        : `/api/bank-accounts/${bank.id}/withdrawals`;
      const body: Record<string, unknown> = {
        amount,
        counterparty_account_code: counterpartyCode,
        description,
        memo: memo || null,
        source_document_url: docUrl,
        source_document_sha256: docSha,
      };
      body[isDeposit ? "deposit_date" : "withdrawal_date"] = date;
      if (fxRate) body.fx_rate = fxRate;
      return api(url, { method: "POST", body });
    },
    onSuccess: () => {
      toast.success(`${isDeposit ? "Deposit" : "Withdrawal"} posted`);
      onPosted();
    },
    onError: (err: Error) => toast.error(`Post failed: ${err.message}`),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isDeposit ? "Record deposit" : "Record withdrawal"} —{" "}
        <span className="text-slate-600 text-base font-normal dark:text-slate-300">
          {bank.name} ({bank.currency_code})
        </span>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <MoneyField
            size="small" label="Amount"
            currency={bank.currency_code}
            value={amount}
            onChange={setAmount}
          />
          <TextField
            size="small"
            label={
              isDeposit
                ? "Source account code (what you're CREDITING)"
                : "Destination account code (what you're DEBITING)"
            }
            value={counterpartyCode}
            onChange={(e) => setCounterpartyCode(e.target.value.trim())}
            helperText={
              isDeposit
                ? "Common: 2200 (loan), 3000 (equity), 4900 (interest income)"
                : "Common: 2200 (loan repayment), 8010 (interest expense), 3060 (distribution)"
            }
          />
          <Stack direction="row" spacing={2}>
            <TextField
              size="small" label="Date" type="date"
              value={date} onChange={(e) => setDate(e.target.value)}
              sx={{ flex: 1 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small" label="FX rate (if bank ≠ base currency)"
              type="number" value={fxRate}
              onChange={(e) => setFxRate(e.target.value)}
              inputProps={{ step: "0.0001", min: 0 }}
              placeholder="1.0"
              sx={{ flex: 1 }}
              helperText={`Rate from ${bank.currency_code} to base`}
            />
          </Stack>
          <TextField
            size="small" label="Description" value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              isDeposit
                ? "e.g. Loan draw #1 from First Republic"
                : "e.g. Principal payment on term loan"
            }
          />
          <TextField
            size="small" label="Memo (optional)" value={memo}
            onChange={(e) => setMemo(e.target.value)}
          />

          {/* Document drop zone */}
          <div
            className="border-2 border-dashed border-slate-300 rounded-md p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 dark:border-gray-700"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) void uploadDoc(f);
            }}
            onClick={() => {
              const inp = document.createElement("input");
              inp.type = "file";
              inp.accept = "application/pdf,image/*";
              inp.onchange = () => {
                const f = inp.files?.[0];
                if (f) void uploadDoc(f);
              };
              inp.click();
            }}
          >
            {uploading ? (
              <div className="text-sm text-slate-600 dark:text-slate-300">Uploading…</div>
            ) : docUrl ? (
              <div>
                <div className="text-sm text-emerald-700 font-medium">
                  ✓ {docName}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 dark:text-slate-400">
                  Click or drop another to replace
                </div>
              </div>
            ) : (
              <div>
                <div className="text-sm text-slate-600 font-medium dark:text-slate-300">
                  📎 Drop or click to attach a document
                </div>
                <div className="text-xs text-slate-500 mt-0.5 dark:text-slate-400">
                  Wire scan, deposit slip, loan draw advice, signed wire instruction
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5 dark:text-slate-500">
                  PDF, JPG, PNG, HEIC up to 25 MB
                </div>
              </div>
            )}
          </div>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={
            !amount || !counterpartyCode || !description ||
            post.isPending || uploading
          }
          onClick={() => post.mutate()}
        >
          {post.isPending ? "Posting…" : isDeposit ? "Post deposit" : "Post withdrawal"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ──────────────────────────────────────────────────────────────
// New bank account dialog
// ──────────────────────────────────────────────────────────────
function NewBankAccountDialog({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const accountsQuery = useQuery(accountsListQueryOptions());
  const cashAccounts = (accountsQuery.data ?? []).filter(
    (a) =>
      a.is_postable && a.is_active && (
        a.account_type === "asset" || a.account_type === "liability"
      ),
  );

  const [name, setName] = useState("");
  const [bankName, setBankName] = useState("");
  const [acctType, setAcctType] = useState<BankAccountType>("checking");
  // Tier 2 — track whether the user has manually overridden the type
  // suggestion. Once they touch the Type dropdown, we stop overwriting
  // it from display-name heuristics.
  const [acctTypeUserSet, setAcctTypeUserSet] = useState(false);
  const [last4, setLast4] = useState("");
  const [routingLast4, setRoutingLast4] = useState("");
  const [swift, setSwift] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [glAccountId, setGlAccountId] = useState("");
  const [openingBalance, setOpeningBalance] = useState("0");
  const [notes, setNotes] = useState("");

  // Tier 2 — instant client-side heuristic on display-name blur.
  // Cheap, no API call. The full backend Suggest button (Tier 1) does
  // the GL account lookup, which can't be done client-side.
  const inferTypeFromName = (n: string): BankAccountType | null => {
    const s = n.toLowerCase();
    if (/\bline of credit\b|\bloc\b/.test(s)) return "credit_line";
    if (/\bmoney\s*market\b|\bmm\b/.test(s)) return "money_market";
    if (/\bsav(ings)?\b/.test(s)) return "savings";
    if (/\bloan\b|\bnote\s+payable\b|\bterm\s+loan\b/.test(s)) return "loan";
    if (/\bpayroll\b|\boperating\b|\bmain\b|\bchecking\b|\bchk\b/.test(s)) return "checking";
    return null;
  };
  const handleNameBlur = () => {
    if (acctTypeUserSet) return;
    const guess = inferTypeFromName(name);
    if (guess && guess !== acctType) setAcctType(guess);
  };

  // Suggest framework — applies one suggestion to local form state.
  const applySuggestion = (field: string, value: unknown, _label?: string | null) => {
    switch (field) {
      case "account_type":
        setAcctType(value as BankAccountType);
        // Treat AI-applied type as "user-confirmed" so blur doesn't fight it
        setAcctTypeUserSet(true);
        break;
      case "gl_account_id":
        if (typeof value === "string") setGlAccountId(value);
        break;
      case "notes":
        if (typeof value === "string") setNotes(value);
        break;
      case "currency_code":
        if (typeof value === "string") setCurrency(value);
        break;
      // future-proofing: silently ignore unknown fields rather than crash
    }
  };

  // "+ Create" handler for a proposed_create suggestion. Here it's
  // always a new GL Account: POST to /api/accounts, refresh the dropdown
  // list, then select the freshly-created row.
  const handleProposeCreate = async (
    field: string, proposed: ProposedCreate,
  ) => {
    if (field !== "gl_account_id") return;
    try {
      const created = await api<{ id: string; code: string; name: string }>(
        proposed.target_endpoint,
        { method: "POST", body: proposed.payload },
      );
      // Pull the freshly-created account into the dropdown
      await qc.invalidateQueries({ queryKey: accountKeys.all });
      setGlAccountId(created.id);
      toast.success(`Created account ${created.code} — ${created.name}`);
    } catch (err) {
      toast.error(`Create failed: ${(err as Error).message}`);
    }
  };

  const create = useMutation({
    mutationFn: async () => {
      const payload: BankAccountCreate = {
        name: name.trim(),
        bank_name: bankName.trim(),
        account_type: acctType,
        account_number_last4: last4 || null,
        routing_number_last4: routingLast4 || null,
        swift_code: swift.trim() || null,
        currency_code: currency.toUpperCase(),
        gl_account_id: glAccountId,
        opening_balance: openingBalance || "0",
        notes: notes.trim() || null,
      };
      return api("/api/bank-accounts", { method: "POST", body: payload });
    },
    onSuccess: () => {
      onCreated();
      onClose();
    },
    onError: (err: Error) => toast.error(`Create failed: ${err.message}`),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <span>New bank account</span>
        <Box sx={{ flex: 1 }} />
        <SuggestButton
          formKind="bank_account"
          buildContext={() => ({
            name: name.trim(),
            bank_name: bankName.trim(),
            account_type: acctType,
            currency_code: currency.toUpperCase(),
            account_number_last4: last4 || null,
          })}
          onApplied={applySuggestion}
          onProposeCreate={handleProposeCreate}
        />
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              size="small" label="Display name" value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={handleNameBlur}
              placeholder="Chase Operating" sx={{ flex: 2 }}
              helperText="✨ Tab out — we'll guess the type"
            />
            <TextField
              size="small" label="Bank" value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="JPMorgan Chase" sx={{ flex: 2 }}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              select size="small" label="Type" value={acctType}
              onChange={(e) => {
                setAcctType(e.target.value as BankAccountType);
                setAcctTypeUserSet(true);
              }}
              sx={{ flex: 2 }}
              helperText={ACCOUNT_TYPES.find((t) => t.value === acctType)?.hint}
            >
              {ACCOUNT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small" label="Currency" value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
              inputProps={{ maxLength: 3 }}
              sx={{ flex: 1 }}
              helperText="ISO 4217"
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              size="small" label="Account # last 4" value={last4}
              onChange={(e) => setLast4(e.target.value.slice(0, 4))}
              inputProps={{ maxLength: 4 }}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small" label="Routing last 4" value={routingLast4}
              onChange={(e) => setRoutingLast4(e.target.value.slice(0, 4))}
              inputProps={{ maxLength: 4 }}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small" label="SWIFT (intl)" value={swift}
              onChange={(e) => setSwift(e.target.value)}
              sx={{ flex: 2 }}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              select size="small" label="GL account (what this bank maps to)"
              value={glAccountId}
              onChange={(e) => setGlAccountId(e.target.value)}
              helperText="Pick a leaf Cash or Liability account, or click ✨ Suggest"
              sx={{ flex: 1 }}
            >
              {cashAccounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {codeAndName(a, " — ")} ({a.account_type})
                </MenuItem>
              ))}
            </TextField>
            <SuggestButton
              formKind="bank_account"
              label="✨"
              buildContext={() => ({
                name: name.trim(),
                bank_name: bankName.trim(),
                account_type: acctType,
                currency_code: currency.toUpperCase(),
                account_number_last4: last4 || null,
              })}
              onApplied={applySuggestion}
              onProposeCreate={handleProposeCreate}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <MoneyField
              size="small" label="Opening balance"
              currency={currency}
              value={openingBalance}
              onChange={setOpeningBalance}
              sx={{ flex: 1 }}
              helperText="Informational — balance reads from the GL"
            />
          </Stack>
          <TextField
            size="small" label="Notes (optional)" value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name || !bankName || !glAccountId || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
